import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  ComposedModal,
  InlineLoading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Tag,
  TextInput,
} from "@carbon/react";
import { CheckmarkFilled, SubtractAlt } from "@carbon/icons-react";

import {
  useLaunchSecondRound,
  useSecondRoundEligibleSuppliers,
  SecondRoundEligibleSupplier,
} from "../../hooks/useOrders";
import { useNotification } from "../../context";
import {
  biddingInstants,
  getBiddingWindowErrors,
  isBiddingWindowValid,
  isScheduledStart,
  todayInArgentina,
} from "../../utils";

type Props = {
  open: boolean;
  onClose: () => void;
  orderId: string;
  onLaunched: () => void;
};


const supplierLabel = (s: SecondRoundEligibleSupplier) =>
  s.companyName ||
  `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() ||
  s.email ||
  s.id;

const formatTotal = (amount: number | null, currency: string | null) => {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: currency ? "currency" : "decimal",
      currency: currency ?? undefined,
      currencyDisplay: currency ? "code" : undefined,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency ? `${currency} ` : ""}${amount.toFixed(2)}`;
  }
};

const helper: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "var(--cds-text-secondary, #525252)",
};

const SecondRoundModal: React.FC<Props> = ({
  open,
  onClose,
  orderId,
  onLaunched,
}) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  // Days default to today → a week from today; the closing time is the buyer's to choose.
  const defaultDates = useMemo(() => {
    const today = todayInArgentina();
    const [year, month, day] = today.split("-").map(Number);
    const finish = new Date(Date.UTC(year, month - 1, day + 7));
    return { start: today, finish: finish.toISOString().slice(0, 10) };
  }, []);

  const [startDate, setStartDate] = useState(defaultDates.start);
  const [finishDate, setFinishDate] = useState(defaultDates.finish);
  const [startTime, setStartTime] = useState("");
  const [finishTime, setFinishTime] = useState("");
  const biddingWindow = {
    startDateValue: startDate,
    startTimeValue: startTime,
    endDateValue: finishDate,
    endTimeValue: finishTime,
  };
  const windowErrors = getBiddingWindowErrors(biddingWindow);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data, isLoading: isLoadingSuppliers } =
    useSecondRoundEligibleSuppliers(orderId, open);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const suppliers: SecondRoundEligibleSupplier[] = data ?? [];
  const hasSuppliers = suppliers.length > 0;

  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasHydratedRef.current = false;
      return;
    }
    if (hasHydratedRef.current) return;
    if (suppliers.length === 0) return;
    setSelectedIds(suppliers.map((s) => s.id));
    hasHydratedRef.current = true;
  }, [open, suppliers]);

  useEffect(() => {
    if (open) {
      setStartDate(defaultDates.start);
      setFinishDate(defaultDates.finish);
      setStartTime("");
      setFinishTime("");
    }
  }, [open, defaultDates]);

  const { mutate: launchSecondRound, isPending: isLaunching } =
    useLaunchSecondRound(
      () => {
        openNotification(t("tenders.data.secondRoundLaunched"));
        onLaunched();
      },
      () => openNotification(t("newMaterialForm.error"))
    );

  const allSelected = hasSuppliers && selectedIds.length === suppliers.length;

  const toggleAll = () => {
    if (!hasSuppliers) return;
    setSelectedIds(allSelected ? [] : suppliers.map((s) => s.id));
  };

  const toggleSupplier = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const datesValid =
    !!startDate &&
    !!finishDate &&
    finishDate >= startDate &&
    isBiddingWindowValid(biddingWindow);
  const supplierSelectionValid = !hasSuppliers || selectedIds.length > 0;
  const canLaunch =
    datesValid && supplierSelectionValid && !isLaunching && !isLoadingSuppliers;

  const validationMessage = (() => {
    if (!startDate || !finishDate)
      return t("tenders.data.secondRoundNeedDates");
    if (finishDate < startDate) return t("tenders.data.secondRoundDateOrder");
    const timeError = windowErrors.startTime ?? windowErrors.endTime;
    if (timeError) return t(timeError);
    if (hasSuppliers && selectedIds.length === 0)
      return t("tenders.data.secondRoundNeedSupplier");
    return null;
  })();

  const handleLaunch = () => {
    if (!canLaunch) return;
    launchSecondRound({
      orderId,
      startDate,
      finishDate,
      ...biddingInstants(biddingWindow),
      supplierIds: hasSuppliers ? selectedIds : undefined,
    });
  };

  return (
    <ComposedModal
      open={open}
      aria-label={t("tenders.data.secondRoundModalTitle")}
      onClose={onClose}
    >
      <ModalHeader title={t("tenders.data.secondRoundModalTitle")} />
      <ModalBody>
        <p style={helper}>{t("tenders.data.secondRoundQuestion")}</p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.75rem",
            paddingTop: "1rem",
          }}
        >
          <TextInput
            id="srStart"
            type="date"
            labelText={`${t("tenders.form.initialDate")} *`}
            value={startDate}
            min={todayInArgentina()}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextInput
            id="srStartTime"
            type="time"
            step={60}
            labelText={`${t("tenders.form.openTime")}${
              isScheduledStart(startDate) ? " *" : ""
            }`}
            helperText={
              isScheduledStart(startDate)
                ? undefined
                : t("tenders.form.openTimeHelper")
            }
            value={startTime}
            invalid={!!windowErrors.startTime}
            invalidText={windowErrors.startTime && t(windowErrors.startTime)}
            onChange={(e) => setStartTime(e.target.value)}
          />
          <TextInput
            id="srFinish"
            type="date"
            labelText={`${t("tenders.form.closeDate")} *`}
            min={startDate || todayInArgentina()}
            value={finishDate}
            onChange={(e) => setFinishDate(e.target.value)}
          />
          <TextInput
            id="srFinishTime"
            type="time"
            step={60}
            labelText={`${t("tenders.form.closeTime")} *`}
            helperText={t("tenders.form.argentinaTime")}
            value={finishTime}
            invalid={!!windowErrors.endTime}
            invalidText={windowErrors.endTime && t(windowErrors.endTime)}
            onChange={(e) => setFinishTime(e.target.value)}
          />
        </div>

        <div style={{ marginTop: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                color: "var(--cds-text-secondary, #525252)",
              }}
            >
              {t("tenders.data.secondRoundSuppliers")}
            </span>
            {hasSuppliers && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <button
                  type="button"
                  onClick={toggleAll}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textTransform: "uppercase",
                    fontSize: "0.625rem",
                    color: "var(--cds-link-primary, #0f62fe)",
                    padding: 0,
                  }}
                >
                  {allSelected
                    ? t("tenders.data.secondRoundDeselectAll")
                    : t("tenders.data.secondRoundSelectAll")}
                </button>
                <span style={{ fontSize: "0.625rem", ...helper }}>
                  {selectedIds.length} / {suppliers.length}
                </span>
              </div>
            )}
          </div>
          <p style={{ ...helper, fontSize: "0.625rem", margin: "0 0 0.5rem" }}>
            {t("tenders.data.secondRoundSuppliersHint")}
          </p>

          {isLoadingSuppliers && !hasSuppliers ? (
            <InlineLoading description={t("cta.loading")} />
          ) : !hasSuppliers ? (
            <p style={{ ...helper, fontStyle: "italic" }}>
              {t("tenders.data.secondRoundSuppliersEmpty")}
            </p>
          ) : (
            <div
              style={{
                maxHeight: "14rem",
                overflowY: "auto",
                border: "1px solid var(--cds-border-subtle, #e0e0e0)",
                borderRadius: 4,
                padding: "0.5rem",
              }}
            >
              {suppliers.map((s) => {
                const checked = selectedIds.includes(s.id);
                const total = formatTotal(s.totalOfferAmount, s.currency);
                return (
                  <div
                    key={s.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Checkbox
                      id={`sr-supplier-${s.id}`}
                      labelText={supplierLabel(s)}
                      checked={checked}
                      onChange={() => toggleSupplier(s.id)}
                    />
                    <span style={{ marginLeft: "auto" }}>
                      {s.submittedOffer ? (
                        <Tag
                          type="green"
                          renderIcon={CheckmarkFilled}
                          size="sm"
                        >
                          {total ?? t("tenders.data.secondRoundOffered")}
                        </Tag>
                      ) : (
                        <Tag type="gray" renderIcon={SubtractAlt} size="sm">
                          {t("tenders.data.secondRoundNoOffer")}
                        </Tag>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {validationMessage && (
          <p
            style={{
              marginTop: "0.75rem",
              fontSize: "0.625rem",
              color: "var(--cds-text-error, #da1e28)",
            }}
          >
            {validationMessage}
          </p>
        )}
      </ModalBody>
      <ModalFooter
        secondaryButtonText={t("cta.cancel")}
        primaryButtonText={t("tenders.data.secondRoundCta")}
        primaryButtonDisabled={!canLaunch}
        loadingStatus={isLaunching ? "active" : "inactive"}
        loadingDescription={t("cta.loading")}
        onRequestSubmit={handleLaunch}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default SecondRoundModal;
